import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PreviewModal = ({ mapping, onClose, onConfirmMapping }) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!mapping) return null;

  const { source, target } = mapping;

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'image': return 'Image';
      case 'document': return 'FileText';
      case 'audio': return 'Volume2';
      default: return 'File';
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    
    // Simulate confirmation delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    onConfirmMapping?.(source?.id, target?.id);
    setIsConfirming(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-tier-3 max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Eye" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Preview de Mapeamento</h2>
                <p className="text-sm text-muted-foreground font-caption">
                  Visualize como a mídia será exibida no contexto do curso
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Source Media Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Archive" size={18} color="var(--color-primary)" />
                <span>Mídia de Origem</span>
              </h3>
              
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="aspect-video bg-muted rounded-lg mb-4 overflow-hidden">
                  {source?.thumbnail || source?.url ? (
                    source?.type === 'video' ? (
                      <video
                        src={source?.url}
                        poster={source?.thumbnail}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : source?.type === 'image' ? (
                      <img
                        src={source?.thumbnail || source?.url}
                        alt={source?.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name={getMediaIcon(source?.type)} size={64} color="var(--color-muted-foreground)" />
                      </div>
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name={getMediaIcon(source?.type)} size={64} color="var(--color-muted-foreground)" />
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-foreground">{source?.title}</h4>
                    <p className="text-sm text-muted-foreground">{source?.filename}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Tipo:</span>
                      <div className="flex items-center space-x-1 mt-1">
                        <Icon name={getMediaIcon(source?.type)} size={14} />
                        <span className="capitalize text-foreground">{source?.type}</span>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-muted-foreground">Tamanho:</span>
                      <div className="text-foreground mt-1">{source?.size || 'N/A'}</div>
                    </div>
                    
                    {source?.duration && (
                      <div>
                        <span className="text-muted-foreground">Duração:</span>
                        <div className="text-foreground mt-1">{source?.duration}</div>
                      </div>
                    )}
                    
                    {source?.dimensions && (
                      <div>
                        <span className="text-muted-foreground">Dimensões:</span>
                        <div className="text-foreground mt-1">{source?.dimensions}</div>
                      </div>
                    )}
                  </div>
                  
                  {source?.tags && source?.tags?.length > 0 && (
                    <div>
                      <span className="text-muted-foreground text-sm">Tags:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {source?.tags?.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Target Lesson Context */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Target" size={18} color="var(--color-secondary)" />
                <span>Contexto da Lição</span>
              </h3>
              
              <div className="bg-secondary/10 rounded-lg p-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground flex items-center space-x-2">
                      <Icon name="FileText" size={16} />
                      <span>{target?.title}</span>
                    </h4>
                    {target?.description && (
                      <p className="text-sm text-muted-foreground mt-1">{target?.description}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                      <span className="text-muted-foreground">Módulo:</span>
                      <span className="font-medium text-foreground">{target?.moduleName}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                      <span className="text-muted-foreground">Curso:</span>
                      <span className="font-medium text-foreground">{target?.courseName}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                      <span className="text-muted-foreground">Tipo de Mídia:</span>
                      <div className="flex items-center space-x-1">
                        <Icon name={getMediaIcon(target?.mediaType)} size={14} />
                        <span className="font-medium text-foreground capitalize">
                          {target?.mediaType || 'Qualquer'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                      <span className="text-muted-foreground">Mídia Obrigatória:</span>
                      <div className="flex items-center space-x-1">
                        <Icon 
                          name={target?.mediaRequired ? 'CheckCircle' : 'Circle'} 
                          size={14} 
                          color={target?.mediaRequired ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                        />
                        <span className={`font-medium ${target?.mediaRequired ? 'text-success' : 'text-muted-foreground'}`}>
                          {target?.mediaRequired ? 'Sim' : 'Não'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Compatibility Check */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h5 className="font-medium text-foreground mb-3">Verificação de Compatibilidade</h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Tipo de mídia:</span>
                        <div className="flex items-center space-x-1">
                          <Icon 
                            name={!target?.mediaType || target?.mediaType === source?.type ? 'CheckCircle' : 'AlertTriangle'} 
                            size={14} 
                            color={!target?.mediaType || target?.mediaType === source?.type ? 'var(--color-success)' : 'var(--color-warning)'}
                          />
                          <span className={!target?.mediaType || target?.mediaType === source?.type ? 'text-success' : 'text-warning'}>
                            {!target?.mediaType || target?.mediaType === source?.type ? 'Compatível' : 'Incompatível'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span>Tamanho do arquivo:</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                          <span className="text-success">Aceitável</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span>Qualidade:</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                          <span className="text-success">Adequada</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview in Course Context */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Monitor" size={18} color="var(--color-accent)" />
              <span>Preview no Contexto do Curso</span>
            </h3>
            
            <div className="bg-muted/30 rounded-lg p-6">
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="FileText" size={16} color="var(--color-muted-foreground)" />
                  <h4 className="font-medium text-foreground">{target?.title}</h4>
                </div>
                
                <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                  {source?.type === 'video' ? (
                    <video
                      src={source?.url}
                      poster={source?.thumbnail}
                     
                      className="w-full h-full object-cover"
                    />
                  ) : source?.type === 'image' ? (
                    <img
                      src={source?.thumbnail || source?.url}
                      alt={source?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <Icon name={getMediaIcon(source?.type)} size={48} color="var(--color-muted-foreground)" />
                      <p className="text-sm text-muted-foreground mt-2">{source?.title}</p>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Esta é uma prévia de como a mídia aparecerá na lição do curso. 
                  A mídia será automaticamente otimizada para a melhor experiência de aprendizado.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {(!target?.mediaType || target?.mediaType === source?.type) ? (
                <div className="flex items-center space-x-2 text-success">
                  <Icon name="CheckCircle" size={16} />
                  <span>Mapeamento compatível e pronto para aplicar</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="AlertTriangle" size={16} />
                  <span>Aviso: Tipos de mídia não coincidem</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isConfirming}
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                iconName="Link"
                loading={isConfirming}
                onClick={handleConfirm}
              >
                Confirmar Mapeamento
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;